import { useEnum } from "@/api/services/global/enum";
import { TTalentListOutputDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { IconActionStar } from "@/assets/icons";
import { Table, TableSorting } from "@/components/elements/Table";
import { getLongestLabelMinWidth } from "@/components/elements/Tag";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { TalentStatusTag } from "@/features/talent/components/talent-common/TalentStatusTag";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { CaseMatchStatusOptions, TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { findLabelByValue } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import React, { FC, useMemo, useEffect, useRef, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CaseMatchStatusTag } from "@/features/talent/components/talent-common/CaseMatchStatusTag";
import { CenteredCell } from "@/components/commons/CenteredCell";
import { TalentAvatarCell } from "@/components/commons/TalentAvatarCell";
import { TALENT_LIST_URL_STORAGE_KEY, TALENT_SELECT_ALL_STORAGE_KEY, TALENT_SELECTED_STORAGE_KEY } from "@/features/talent/constants/common";
import { TTalentDisplayColumnSetting } from "@/features/talent/components/talent-list/talent-filter-shared";

type TTalentListTableProps = {
  rows: TTalentListOutputDto[];
  isLoading: boolean;
  onSelectionChange?: (selectedIds: number[], isAllSelected: boolean) => void;
  displayColumnSettings?: TTalentDisplayColumnSetting[] | null;
  selectionResetKey?: number;
};

export const TalentListTable: FC<TTalentListTableProps> = ({ rows, isLoading, onSelectionChange, displayColumnSettings, selectionResetKey }) => {
  const { data: genderOptions } = useEnum("EGender");
  const { data: recruitmentStatusOptions } = useEnum("ERecruitmentStatus");
  const { data: educationLevelOptions } = useEnum("EEducationLevel");
  // Auto-fit every status pill to the longest recruitment-status label so all share one width.
  const recruitmentStatusTagMinWidth = useMemo(
    () => getLongestLabelMinWidth((recruitmentStatusOptions ?? []).map((option) => option.label)),
    [recruitmentStatusOptions],
  );
  const navigate = useNavigate();
  const location = useLocation();

  const rowsRef = useRef(rows);
  const isSyncingSelection = useRef(false);
  const isInitialMount = useRef(true);

  // Track if user clicked "Select All" button
  const userClickedSelectAll = useRef(false);
  const prevIsAllRowsSelected = useRef(false);
  const allRowIdsWhenSelectAll = useRef<number[]>([]);
  const prevSelectedCount = useRef(0);
  const [heightTable] = useState<string>("calc(100vh - 343px)");

  const [isSelectAllMode, setIsSelectAllMode] = useState<boolean>(() => {
    return sessionStorage.getItem(TALENT_SELECT_ALL_STORAGE_KEY) === "true";
  });

  const [selectedIds, setSelectedIds] = useState<number[]>(() => {
    const raw = sessionStorage.getItem(TALENT_SELECTED_STORAGE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return Object.values(parsed).flat() as number[];
    } catch {
      return [];
    }
  });

  const columns = useMemo<MRT_ColumnDef<TTalentListOutputDto>[]>(
    () => [
      {
        id: "recruitmentStatus",
        header: "ステータス",
        accessorKey: "recruitmentStatus",
        Cell: ({ row }) => (
          <CenteredCell>
            <TalentStatusTag
              status={row.original?.recruitmentStatus}
              title={findLabelByValue(recruitmentStatusOptions, row.original.recruitmentStatus)}
              variant="bold"
              minWidth={recruitmentStatusTagMinWidth}
            />
          </CenteredCell>
        ),
        Header: <TableSorting name="talent.recruitmentStatus">ステータス</TableSorting>,
      },
      {
        id: "talentNumber",
        header: "人材管理番号",
        Header: <TableSorting name="talent.talentNumber">人材管理番号</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.talentNumber ? truncateWithEllipsis(row.original.talentNumber, 20) : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        accessorKey: "talentNumber",
      },
      {
        id: "fullName",
        accessorKey: "fullName",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center" gap={1}>
              <TalentAvatarCell filePath={row.original.avatarThumbnailPath} name={row.original.fullName} />
              {row.original.isRecommendedTalent && (
                <Box sx={{ flexShrink: 0, minWidth: "24px", minHeight: "25px", display: "flex", alignItems: "center" }}>
                  <IconActionStar width={24} height={25} />
                </Box>
              )}
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.fullName ? truncateWithEllipsis(row.original.fullName, 20) : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        header: "氏名",
        Header: <TableSorting name="talent.fullName">氏名</TableSorting>,
        muiTableHeadCellProps: {
          sx: { maxWidth: 220 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 220 },
        },
      },
      {
        id: "nickName",
        accessorKey: "nickName",
        Cell: ({ row }) => {
          return (
            <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
              {row.original?.nickName ? truncateWithEllipsis(row.original.nickName, 10) : EMPTY_STR.TEXT}
            </Typography>
          );
        },
        header: "呼び名",
        Header: <TableSorting name="talent.nickName">呼び名</TableSorting>,
        muiTableHeadCellProps: {
          sx: { maxWidth: 120 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 120 },
        },
      },
      {
        id: "nationalityName",
        // Use accessorFn instead of dot-path accessorKey so MRT doesn't warn
        // when a row's `nationality` is null/undefined.
        accessorFn: (row) => row.nationality?.nationalityName ?? "",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.nationality?.nationalityName || EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        header: "国籍",
        Header: <TableSorting name="nationality.nationalityName">国籍</TableSorting>,
      },
      {
        id: "gender",
        accessorKey: "gender",
        header: "性別",
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original?.gender),
      },
      {
        id: "caseMatchStatus",
        header: "最新進捗",
        accessorKey: "caseMatchStatus",
        Cell: ({ row }) => {
          return (
            <CenteredCell>
              <CaseMatchStatusTag
                status={row.original?.caseMatchStatus ?? -1}
                title={findLabelByValue(CaseMatchStatusOptions, row.original?.caseMatchStatus) || "提案なし"}
                variant="bold"
              />
            </CenteredCell>
          );
        },
        Header: <TableSorting name="caseMatchStatus">最新進捗</TableSorting>,
      },
      {
        id: "residenceStatusId",
        header: "在留資格",
        Cell: ({ row }) => row.original?.residenceStatus?.residenceStatus || EMPTY_STR.TEXT,
        Header: <TableSorting name="residenceStatus.residenceStatus">在留資格</TableSorting>,
      },
      {
        id: "languageCertificates",
        header: "日本語能力",
        Cell: ({ row }) => {
          if (!row.original.languageCertificates || row.original.languageCertificates.length === 0) {
            return EMPTY_STR.TEXT;
          }

          return row.original.languageCertificates ?? EMPTY_STR.TEXT;
        },
        Header: <TableSorting name="languageTestLevel.level">日本語能力</TableSorting>,
      },
      {
        id: "companySpecificIndustry",
        header: "分野",
        Cell: ({ row }) => {
          return row.original.companySpecificIndustry?.companySpecificIndustry || EMPTY_STR.TEXT;
        },
        Header: <TableSorting name="companySpecificIndustry.companySpecificIndustry">分野</TableSorting>,
        muiTableHeadCellProps: {
          sx: { maxWidth: 120 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 120 },
        },
      },
      {
        id: "location",
        header: "居住地",
        accessorKey: "location",
        Cell: ({ row }) => {
          const option = TALENT_LOCATION_OPTIONS.find((o) => o.value === row.original?.location);
          return option?.label || EMPTY_STR.TEXT;
        },
        Header: <TableSorting name="talent.location">居住地</TableSorting>,
      },
      {
        id: "personInChargeId",
        header: "担当者",
        accessorKey: "personInCharge.fullName",
        Cell: ({ row }) => {
          return (
            <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
              {row.original?.personInCharge?.fullName ? truncateWithEllipsis(row.original.personInCharge.fullName, 20) : EMPTY_STR.TEXT}
            </Typography>
          );
        },
        Header: <TableSorting name="personInCharge.fullName">担当者</TableSorting>,
      },
      {
        id: "createdAt",
        header: "登録日",
        Cell: ({ row }) => dayjs(row.original.createdAt).format(DATE_FORMAT),
        Header: <TableSorting name="talent.createdAt">登録日</TableSorting>,
      },
      {
        id: "desiredWorkLocation",
        header: "希望勤務地",
        accessorKey: "desiredWorkLocation",
        Cell: ({ row }) => (row.original?.desiredWorkLocation ? truncateWithEllipsis(row.original?.desiredWorkLocation, 20) : EMPTY_STR.TEXT),
        Header: <TableSorting name="talent.desiredWorkLocation">希望勤務地</TableSorting>,
      },
      {
        id: "heldQualifications",
        header: "資格",
        Cell: ({ row }) => {
          const qualifications = row.original?.heldQualifications;
          if (!qualifications || qualifications.length === 0) return EMPTY_STR.TEXT;
          return truncateWithEllipsis(qualifications.join(", "), 20);
        },
        Header: <TableSorting name="talent.heldQualifications">資格</TableSorting>,
      },
      {
        id: "educationLevel",
        header: "学歴",
        Header: <TableSorting name="educationBackgrounds.educationLevelId">学歴</TableSorting>,
        Cell: ({ row }) => {
          const entries = row.original?.educationBackgrounds;
          if (!entries || entries.length === 0) return EMPTY_STR.TEXT;
          const joined = entries
            .map((entry) => {
              const levelLabel = entry.educationLevelId
                ? findLabelByValue(educationLevelOptions, entry.educationLevelId)
                : null;
              const school = entry.schoolName ?? "";
              return [school, levelLabel].filter(Boolean).join(" ");
            })
            .filter(Boolean)
            .join(" / ");
          return truncateWithEllipsis(joined, 20);
        },
      },
      {
        id: "partnerName",
        header: "パートナー名",
        Cell: ({ row }) => (row.original?.partner?.partnerName ? truncateWithEllipsis(row.original.partner.partnerName, 20) : EMPTY_STR.TEXT),
        Header: <TableSorting name="partner.partnerName">パートナー名</TableSorting>,
      },
    ],
    [genderOptions, recruitmentStatusOptions, recruitmentStatusTagMinWidth, educationLevelOptions],
  );

  const allColumns = columns;

  const toEditRoute = useCallback(
    (talentId: number) => {
      sessionStorage.setItem(TALENT_LIST_URL_STORAGE_KEY, location.pathname + location.search);
      const route = getDynamicRoute(AppRoutes.talentDetail, { talentId: talentId });
      navigate(route);
    },
    [navigate, location.pathname, location.search],
  );

  const [forceIndeterminate, setForceIndeterminate] = useState(false);

  const { table } = useTable({
    columns: allColumns,
    rows,
    enableRowSelection: true,
    getRowId: (row) => row.id.toString(),
    pinningColumnsLeft: ["mrt-row-select"],
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    onClickRow: (row) => toEditRoute(row.id),
    renderEmptyRowsFallback: ({}) => <Box></Box>,
    muiSelectAllCheckboxProps: ({ table }) => ({
      indeterminate: forceIndeterminate && table.getIsAllRowsSelected(),
      checked: isSelectAllMode || (!forceIndeterminate && table.getIsAllRowsSelected()),
      onChange: (event) => {
        if (event.target.checked) {
          setForceIndeterminate(false);
          userClickedSelectAll.current = true;
          allRowIdsWhenSelectAll.current = rows.map((row) => row.id);
          setIsSelectAllMode(true);
          sessionStorage.setItem(TALENT_SELECT_ALL_STORAGE_KEY, "true");
          sessionStorage.setItem(TALENT_SELECTED_STORAGE_KEY, JSON.stringify([]));
          setSelectedIds([]);
        } else {
          setForceIndeterminate(false);
          userClickedSelectAll.current = false;
          allRowIdsWhenSelectAll.current = [];
          setIsSelectAllMode(false);
          sessionStorage.setItem(TALENT_SELECT_ALL_STORAGE_KEY, "false");
          sessionStorage.setItem(TALENT_SELECTED_STORAGE_KEY, JSON.stringify([]));
          setSelectedIds([]);
        }
        table.getToggleAllRowsSelectedHandler()(event);
      },
    }),
  });

  // Reset internal selection state when parent signals a reset (e.g. after deletion)
  const prevResetKey = useRef(selectionResetKey);
  useEffect(() => {
    if (selectionResetKey === prevResetKey.current) return;
    prevResetKey.current = selectionResetKey;

    isSyncingSelection.current = true;
    setIsSelectAllMode(false);
    setSelectedIds([]);
    userClickedSelectAll.current = false;
    allRowIdsWhenSelectAll.current = [];
    setForceIndeterminate(false);
    prevIsAllRowsSelected.current = false;
    prevSelectedCount.current = 0;
    table.setRowSelection({});
  }, [selectionResetKey, table]);

  useEffect(() => {
    if (!displayColumnSettings) return;

    const visibleIds = displayColumnSettings.filter((s) => s.visible).map((s) => s.id);
    table.setColumnOrder(["mrt-row-select", ...visibleIds]);

    const visibilityMap: Record<string, boolean> = {};
    displayColumnSettings.forEach((s) => {
      visibilityMap[s.id] = s.visible;
    });
    table.setColumnVisibility(visibilityMap);
  }, [displayColumnSettings, table]);

  const tableSelectedIds = React.useMemo(() => table.getSelectedRowModel().rows.map((row) => row.original.id), [table.getSelectedRowModel().rows]);

  const currentPageSelectedIds = useMemo(() => {
    const currentRowIds = rows.map((r) => r.id);
    return selectedIds.filter((id: number) => currentRowIds.includes(id));
  }, [rows, selectedIds]);

  useEffect(() => {
    const prevRowIds = rowsRef.current.map((r) => r.id).sort((a, b) => a - b);
    const currentRowIds = rows.map((r) => r.id).sort((a, b) => a - b);
    const rowsChanged = prevRowIds.length !== currentRowIds.length || prevRowIds.some((id, idx) => id !== currentRowIds[idx]);

    if (isInitialMount.current || rowsChanged) {
      isInitialMount.current = false;
      rowsRef.current = rows;
      isSyncingSelection.current = true;

      const expectedSelection: MRT_RowSelectionState = {};

      if (isSelectAllMode) {
        const excludedRaw = sessionStorage.getItem(TALENT_SELECTED_STORAGE_KEY);
        const excludedIds: number[] = excludedRaw ? JSON.parse(excludedRaw) : [];
        rows.forEach((row) => {
          if (!excludedIds.includes(row.id)) {
            expectedSelection[row.id.toString()] = true;
          }
        });
        userClickedSelectAll.current = true;
      } else {
        currentPageSelectedIds.forEach((id: number) => {
          expectedSelection[id.toString()] = true;
        });
        userClickedSelectAll.current = false;
      }

      table.setRowSelection(expectedSelection);
      allRowIdsWhenSelectAll.current = [];
    }
  }, [rows, currentPageSelectedIds, table, isSelectAllMode]);

  // Track "Select All" state
  useEffect(() => {
    if (isSyncingSelection.current) return;

    const currentIsAllRowsSelected = table.getIsAllRowsSelected();
    const currentSelectedCount = tableSelectedIds.length;

    if (!prevIsAllRowsSelected.current && currentIsAllRowsSelected) {
      // All rows are selected — show indeterminate if it was via individual clicks (not "Select All" checkbox)
      if (!userClickedSelectAll.current) {
        setForceIndeterminate(true);
      }
    }

    if (currentSelectedCount === 0) {
      userClickedSelectAll.current = false;
      allRowIdsWhenSelectAll.current = [];
      setForceIndeterminate(false);
    }

    prevIsAllRowsSelected.current = currentIsAllRowsSelected;
    prevSelectedCount.current = currentSelectedCount;
  }, [table.getIsAllRowsSelected(), tableSelectedIds.length, rows]);

  // Handle user selection changes and persist to sessionStorage
  useEffect(() => {
    if (isSyncingSelection.current) return;

    const currentRowIds = rows.map((r) => r.id);
    const newSelectedIds = tableSelectedIds;
    const deselectedIds = currentRowIds.filter((id) => !newSelectedIds.includes(id));

    if (isSelectAllMode) {
      setSelectedIds((prev: number[]) => {
        const otherPageExcluded = prev.filter((id) => !currentRowIds.includes(id));
        const thisPageExcluded = deselectedIds;
        const updated = [...otherPageExcluded, ...thisPageExcluded];

        if (updated.length === prev.length && updated.every((id) => prev.includes(id))) {
          return prev;
        }
        sessionStorage.setItem(TALENT_SELECTED_STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
      return;
    }

    setSelectedIds((prev: number[]) => {
      const filteredPrev = prev.filter((id: number) => !deselectedIds.includes(id));
      const merged = Array.from(new Set([...filteredPrev, ...newSelectedIds]));

      sessionStorage.setItem(TALENT_SELECTED_STORAGE_KEY, JSON.stringify(merged));

      return merged;
    });
  }, [tableSelectedIds, rows, isSelectAllMode]);

  // Notify parent of selection changes - separate effect to avoid setState during render
  useEffect(() => {
    if (isSyncingSelection.current) return;

    if (isSelectAllMode) {
      onSelectionChange?.(selectedIds, true);
    } else if (userClickedSelectAll.current) {
      const uncheckedIds = allRowIdsWhenSelectAll.current.filter((id) => !tableSelectedIds.includes(id));
      onSelectionChange?.(uncheckedIds, true);
    } else {
      onSelectionChange?.(selectedIds, false);
    }
  }, [selectedIds, tableSelectedIds, onSelectionChange, isSelectAllMode]);

  useEffect(() => {
    if (isSyncingSelection.current) {
      isSyncingSelection.current = false;
    }
  });

  return <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} headRowHeight={37} bodyRowHeight={42} />;
};
